import type {
  Reduction,
  ReferencePrice,
  LowestPriorPriceHint,
  SizePrice,
  ProductPrice,
} from "../../aysa_api/models/product_price.types";
import { Reader, Writer } from "protobufjs/minimal";
import type { Long } from "protoc-gen-ts";

export const createBaseReduction = (): Reduction => {
  return {
    priceBeforeWithTax: 0,
    reductionAmountWithTax: 0,
    reductionPercentage: 0,
  };
};

export const decodeReduction = (reader: Reader, length: number): Reduction => {
  const end = reader.pos + length;
  const message = createBaseReduction();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.priceBeforeWithTax = (reader.int64() as Long).toNumber();

        break;
      case 2:
        message.reductionAmountWithTax = (reader.int64() as Long).toNumber();

        break;
      case 3:
        message.reductionPercentage = (reader.int64() as Long).toNumber();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeReduction = (writer: Writer, message: Reduction): Writer => {
  writer.uint32(8).int64(message.priceBeforeWithTax);
  writer.uint32(16).int64(message.reductionAmountWithTax);
  writer.uint32(24).int64(message.reductionPercentage);

  return writer;
};

export const createBaseReferencePrice = (): ReferencePrice => {
  return {
    withTax: 0,
    size: 0,
    unit: "",
  };
};

export const decodeReferencePrice = (
  reader: Reader,
  length: number
): ReferencePrice => {
  const end = reader.pos + length;
  const message = createBaseReferencePrice();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.withTax = (reader.int64() as Long).toNumber();

        break;
      case 2:
        message.size = (reader.int64() as Long).toNumber();

        break;
      case 3:
        message.unit = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeReferencePrice = (
  writer: Writer,
  message: ReferencePrice
): Writer => {
  writer.uint32(8).int64(message.withTax);
  writer.uint32(16).int64(message.size);
  writer.uint32(26).string(message.unit);

  return writer;
};

export const createBaseLowestPriorPriceHint = (): LowestPriorPriceHint => {
  return {
    label: "",
  };
};

export const decodeLowestPriorPriceHint = (
  reader: Reader,
  length: number
): LowestPriorPriceHint => {
  const end = reader.pos + length;
  const message = createBaseLowestPriorPriceHint();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.label = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeLowestPriorPriceHint = (
  writer: Writer,
  message: LowestPriorPriceHint
): Writer => {
  writer.uint32(10).string(message.label);

  return writer;
};

export const createBaseSizePrice = (): SizePrice => {
  return {
    withTax: 0,
    tax: 0,
    currencyCode: "",
  };
};

export const decodeSizePrice = (reader: Reader, length: number): SizePrice => {
  const end = reader.pos + length;
  const message = createBaseSizePrice();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.withTax = (reader.int64() as Long).toNumber();

        break;
      case 2:
        message.tax = (reader.int64() as Long).toNumber();

        break;
      case 3:
        message.currencyCode = reader.string();

        break;
      case 4:
        message.referencePrice = decodeReferencePrice(reader, reader.uint32());

        break;
      case 5:
        message.saleReduction = decodeReduction(reader, reader.uint32());

        break;
      case 6:
        message.campaignReduction = decodeReduction(reader, reader.uint32());

        break;
      case 7:
        message.totalReduction = decodeReduction(reader, reader.uint32());

        break;
      case 9:
        message.secondaryPriceLabel = reader.string();

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeSizePrice = (writer: Writer, message: SizePrice): Writer => {
  writer.uint32(8).int64(message.withTax);
  writer.uint32(16).int64(message.tax);
  writer.uint32(26).string(message.currencyCode);
  if (message.referencePrice !== undefined) {
    encodeReferencePrice(
      writer.uint32(34).fork(),
      message.referencePrice
    ).ldelim();
  }
  if (message.saleReduction !== undefined) {
    encodeReduction(writer.uint32(42).fork(), message.saleReduction).ldelim();
  }
  if (message.campaignReduction !== undefined) {
    encodeReduction(
      writer.uint32(50).fork(),
      message.campaignReduction
    ).ldelim();
  }
  if (message.totalReduction !== undefined) {
    encodeReduction(writer.uint32(58).fork(), message.totalReduction).ldelim();
  }
  if (message.secondaryPriceLabel !== undefined) {
    writer.uint32(74).string(message.secondaryPriceLabel);
  }

  return writer;
};

export const createBaseProductPrice = (): ProductPrice => {
  return {
    minPrice: createBaseSizePrice(),
    hasDifferentPrices: false,
  };
};

export const decodeProductPrice = (
  reader: Reader,
  length: number
): ProductPrice => {
  const end = reader.pos + length;
  const message = createBaseProductPrice();

  while (reader.pos < end) {
    const tag = reader.uint32();
    switch (tag >>> 3) {
      case 1:
        message.minPrice = decodeSizePrice(reader, reader.uint32());

        break;
      case 2:
        message.hasDifferentPrices = reader.bool();

        break;
      case 3:
        message.lowestPriorPriceHint = decodeLowestPriorPriceHint(
          reader,
          reader.uint32()
        );

        break;

      default:
        reader.skipType(tag & 7);
        break;
    }
  }

  return message;
};

export const encodeProductPrice = (
  writer: Writer,
  message: ProductPrice
): Writer => {
  encodeSizePrice(writer.uint32(10).fork(), message.minPrice).ldelim();
  writer.uint32(16).bool(message.hasDifferentPrices);
  if (message.lowestPriorPriceHint !== undefined) {
    encodeLowestPriorPriceHint(
      writer.uint32(26).fork(),
      message.lowestPriorPriceHint
    ).ldelim();
  }

  return writer;
};
